<template>
  <div class="status-manager">
    <draggable
      v-model="localStatuses"
      handle=".drag-handle"
      @end="onReorder"
    >
      <div
        v-for="status in localStatuses"
        :key="status.id"
        class="status-item d-flex align-items-center mb-2 p-2 border rounded"
      >
        <feather-icon
          icon="MenuIcon"
          size="16"
          class="drag-handle cursor-move mr-2 text-muted"
        />

        <input
          type="color"
          :value="status.color || '#94a3b8'"
          class="status-color-dot mr-2"
          @change="updateStatusColor(status.id, $event.target.value)"
        >

        <div class="flex-grow-1">
          <span
            v-if="editingId !== status.id"
            class="font-weight-bold"
          >
            {{ status.name }}
          </span>
          <b-form-input
            v-else
            v-model="editForm.name"
            size="sm"
            @keyup.enter="saveStatus"
            @keyup.escape="cancelEdit"
          />
        </div>

        <b-badge
          v-if="status.isFinal"
          variant="success"
          class="mr-2"
        >
          {{ $t('Final') }}
        </b-badge>

        <b-button-group
          v-if="editingId !== status.id"
          size="sm"
        >
          <b-button
            v-b-tooltip.hover="'Edit'"
            variant="flat-primary"
            class="p-50"
            @click="startEdit(status)"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
            />
          </b-button>
          <b-button
            v-b-tooltip.hover="'Toggle Final'"
            variant="flat-secondary"
            class="p-50"
            @click="toggleFinal(status)"
          >
            <feather-icon
              :icon="status.isFinal ? 'CheckCircleIcon' : 'CircleIcon'"
              size="14"
            />
          </b-button>
          <b-button
            v-b-tooltip.hover="'Delete'"
            variant="flat-danger"
            class="p-50"
            :disabled="localStatuses.length <= 1"
            @click="confirmDelete(status)"
          >
            <feather-icon
              icon="TrashIcon"
              size="14"
            />
          </b-button>
        </b-button-group>

        <b-button-group
          v-else
          size="sm"
        >
          <b-button
            variant="flat-success"
            class="p-50"
            @click="saveStatus"
          >
            <feather-icon
              icon="CheckIcon"
              size="14"
            />
          </b-button>
          <b-button
            variant="flat-secondary"
            class="p-50"
            @click="cancelEdit"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </b-button>
        </b-button-group>
      </div>
    </draggable>

    <!-- Add New Status -->
    <div
      v-if="canManageBoards"
      class="add-status border rounded-lg p-1"
    >
      <b-form
        inline
        @submit.prevent="addStatus"
      >
        <b-form-input
          v-model="newStatus.name"
          :placeholder="$t('New status name')"
          size="sm"
          class="mr-2"
          style="width: 200px"
        />
        <b-form-input
          v-model="newStatus.color"
          type="color"
          size="sm"
          class="mr-2"
          style="width: 50px; padding: 2px"
        />
        <custom-switch
          v-model="newStatus.isFinal"
          variant="primary"
          :right-text="$t('Final')"
          :show-icons="false"
          class="mr-2"
        />
        <b-button
          type="submit"
          variant="primary"
          size="sm"
          :disabled="!newStatus.name.trim() || submitting"
        >
          <b-spinner
            v-if="submitting"
            small
            class="mr-50"
          />
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('Add') }}
        </b-button>
      </b-form>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      :title="$t('Delete Status')"
      hide-footer
      centered
    >
      <p>{{ $t('Are you sure you want to delete this status?') }}</p>
      <p class="text-danger">
        <strong>{{ statusToDelete?.name }}</strong>
      </p>

      <div
        v-if="hasTasksInStatus"
        class="mb-3"
      >
        <p class="text-warning">
          {{ $t('This status has tasks. Select a status to reassign them:') }}
        </p>
        <b-form-select
          v-model="reassignStatusId"
          :options="reassignOptions"
          required
        />
      </div>

      <div class="d-flex justify-content-end">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="showDeleteModal = false"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="(hasTasksInStatus && !reassignStatusId) || submitting"
          @click="deleteStatus"
        >
          <b-spinner
            v-if="submitting"
            small
            class="mr-50"
          />
          {{ $t('Delete') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BFormInput,
  BFormSelect,
  BForm,
  BButton,
  BButtonGroup,
  BBadge,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import CustomSwitch from '@/views/common/components/CustomSwitch.vue'
import draggable from 'vuedraggable'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'StatusManager',
  components: {
    BFormInput,
    BFormSelect,
    BForm,
    BButton,
    BButtonGroup,
    BBadge,
    BSpinner,
    CustomSwitch,
    draggable,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    board: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localStatuses: [],
      editingId: null,
      editForm: {
        name: '',
        color: '',
        isFinal: false,
      },
      newStatus: {
        name: '',
        color: '#94a3b8',
        isFinal: false,
      },
      showDeleteModal: false,
      statusToDelete: null,
      reassignStatusId: null,
      submitting: false,
    }
  },
  computed: {
    ...mapGetters('permissions', ['canManageBoards']),
    ...mapGetters('project', ['GET_CURRENT_PROJECT']),
    hasTasksInStatus() {
      if (!this.statusToDelete || !this.board?.tasks?.data) return false
      return this.board.tasks.data.some(t => t.status?.id === this.statusToDelete.id)
    },
    reassignOptions() {
      return this.localStatuses
        .filter(s => s.id !== this.statusToDelete?.id)
        .map(s => ({
          value: s.id,
          text: s.name,
        }))
    },
  },
  watch: {
    board: {
      immediate: true,
      handler(val) {
        if (val?.statuses) {
          this.localStatuses = [...val.statuses].sort((a, b) => a.position - b.position)
        }
      },
    },
  },
  methods: {
    ...mapActions('boards', ['createTaskStatus', 'updateTaskStatus', 'deleteTaskStatus']),
    startEdit(status) {
      this.editingId = status.id
      this.editForm = {
        name: status.name,
        color: status.color,
        isFinal: status.isFinal,
      }
    },
    cancelEdit() {
      this.editingId = null
      this.editForm = { name: '', color: '', isFinal: false }
    },
    async saveStatus() {
      if (!this.editForm.name.trim()) return

      this.submitting = true
      try {
        const result = await this.updateTaskStatus({
          boardUid: this.board.uuid,
          projectUid: this.GET_CURRENT_PROJECT.uuid,
          input: {
            id: this.editingId,
            name: this.editForm.name,
            color: this.editForm.color,
            isFinal: this.editForm.isFinal,
          },
        })
        this.showSuccess(result.message)
        this.$emit('updated')
        this.cancelEdit()
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    async toggleFinal(status) {
      this.submitting = true
      try {
        const result = await this.updateTaskStatus({
          boardUid: this.board.uuid,
          projectUid: this.GET_CURRENT_PROJECT.uuid,
          input: {
            id: status.id,
            isFinal: !status.isFinal,
          },
        })
        this.showSuccess(result.message)
        this.$emit('updated')
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    async updateStatusColor(statusId, color) {
      this.submitting = true
      try {
        const result = await this.updateTaskStatus({
          boardUid: this.board.uuid,
          projectUid: this.GET_CURRENT_PROJECT.uuid,
          input: {
            id: statusId,
            color,
          },
        })
        this.showSuccess(result.message)
        this.$emit('updated')
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    async addStatus() {
      if (!this.newStatus.name.trim()) return

      this.submitting = true
      try {
        const result = await this.createTaskStatus({
          boardUid: this.board.uuid,
          projectUid: this.GET_CURRENT_PROJECT.uuid,
          input: {
            key: this.newStatus.name.toLowerCase().replace(/\s+/g, '_'),
            name: this.newStatus.name,
            color: this.newStatus.color,
            position: this.localStatuses.length,
            isFinal: this.newStatus.isFinal,
          },
        })
        this.newStatus = { name: '', color: '#94a3b8', isFinal: false }
        this.showSuccess(result.message)
        this.$emit('updated')
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    confirmDelete(status) {
      this.statusToDelete = status
      this.reassignStatusId = null
      this.showDeleteModal = true
    },
    async deleteStatus() {
      this.submitting = true
      try {
        const result = await this.deleteTaskStatus({
          id: this.statusToDelete.id,
          reassignStatusId: this.reassignStatusId,
        })
        this.showDeleteModal = false
        this.statusToDelete = null
        this.showSuccess(result.message)
        this.$emit('updated')
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    async onReorder() {
      // Update positions after drag
      const updatePromises = this.localStatuses
        .map((status, i) => {
          if (status.position !== i) {
            return this.updateTaskStatus({
              projectUid: this.GET_CURRENT_PROJECT.uuid,
              boardUid: this.board.uuid,
              input: {
                id: status.id,
                position: i,
              },
            }).catch(error => {
              console.error('Failed to update position:', error)
            })
          }
          return Promise.resolve()
        })

      await Promise.all(updatePromises)
      this.$emit('updated')
    },
  },
}
</script>

<style lang="scss" scoped>
.status-manager {
  .status-color-dot {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
    padding: 0;
    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }
    &::-webkit-color-swatch {
      border: none;
      border-radius: 2px;
    }
  }

  .drag-handle {
    cursor: move;
  }
}
</style>
